import apiClient from "config/apiClient";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import { toSnakeCase } from "util/helper";
import Notify from "config/notify";
import {
  DeleteWheelImage,
  MstItemDetailWheel,
  MstItemDetailWheelSelect,
  MstItemDetailWheelSelectResponse,
  UploadWheelImage,
} from "../types";

/**
 *  get wheel list
 * @param body WheelQuery
 */
export default async function getWheelListApi(
  body?: MstItemDetailWheelSelect,
): Promise<MstItemDetailWheelSelectResponse> {
  const res = await apiClient.post<MstItemDetailWheelSelectResponse>(
    `mst/item/detail/wheel/select`,
    toSnakeCase({
      limit: body?.limit || DEFAULT_LIMIT_ITEMS,
      offset: body?.offset || 1,
      ...(body?.brand_name && { brand_name: body?.brand_name }),
      ...(body?.item_cd && { item_cd: body?.item_cd }),
      ...(body?.maker_name && { maker_name: body?.maker_name }),
    }),
  );
  if (res.data) {
    return res.data;
  }
  return {
    list: {
      maker_list: [],
      mst_item_detail_wheel: [],
      mst_proc_fee: [],
    },
    total: 0,
  };
}

export interface WheelUpdateRequest {
  id: number;
  wheelKbnCd: string;
  wheelKbn: string;
  wheelName: string;
  seq: number;
  statusFlag: number;
  deleteFlag: number;
}

/**
 * Updates a wheel using the provided request body.
 * @param {WheelUpdateRequest} wheel - The request body containing the wheel update details.
 * @returns {Promise<void>} A promise that resolves when the wheel update is complete.
 */
export const updateWheelApi = async (wheel: MstItemDetailWheel): Promise<void> => {
  const response = await apiClient.post(
    `mst/item/detail/wheel/update`,
    toSnakeCase({
      ...(wheel?.item_cd !== undefined && { item_cd: wheel?.item_cd }),
      ...(wheel?.wheel_size !== undefined && { wheel_size: wheel?.wheel_size }),
      ...(wheel?.wheel_width !== undefined && { wheel_width: Number(wheel?.wheel_width) }),
      ...(wheel?.wheel_pcd !== undefined && { wheel_pcd: Number(wheel?.wheel_pcd) }),
      ...(wheel?.wheel_hole !== undefined && { wheel_hole: Number(wheel?.wheel_hole) }),
      ...(wheel?.wheel_inset !== undefined && { wheel_inset: Number(wheel?.wheel_inset) }),
      ...(wheel?.wheel_rim_diam !== undefined && { wheel_rim_diam: Number(wheel?.wheel_rim_diam) }),
      ...(wheel?.wheel_hub_diam !== undefined && { wheel_hub_diam: Number(wheel?.wheel_hub_diam) }),
      ...(wheel?.wheel_color_name !== undefined && { wheel_color_name: wheel?.wheel_color_name }),
      ...(wheel?.import_car_flag !== undefined && {
        import_car_flag: Number(wheel?.import_car_flag),
      }),
      ...(wheel?.low_price_flag !== undefined && { low_price_flag: Number(wheel?.low_price_flag) }),
      ...(wheel?.recommend_flag !== undefined && { recommend_flag: Number(wheel?.recommend_flag) }),
      ...(wheel?.status_flag !== undefined && { status_flag: Number(wheel?.status_flag) }),
      ...(wheel?.remarks !== undefined && { remarks: wheel?.remarks }),
      //   ...(wheel?.jan_cd !== undefined && { jan_cd: wheel?.jan_cd }),
    }),
  );

  if (response.status === 200) {
    Notify.setNotification({
      show: true,
      title: "更新が完了しました",
      confirmLabel: "OK",
      actionsPosition: "center",
    });
  }
};

/**
 * Uploads an image for a wheel.
 * @param {UploadWheelImage} body - The request body containing the image file and brand code.
 * @returns {Promise<{ data: any; status: number }>} A promise that resolves with the response data and status.
 */
export const uploadImageApi = async (
  body: UploadWheelImage,
): Promise<{ data: any; status: number }> => {
  if (!body.image_file) {
    throw new Error("File is required");
  }

  const formData = new FormData();
  formData.append("item_cd", body.item_cd);
  formData.append("image_file", body.image_file);

  try {
    const response = await apiClient.post("/mst/item/detail/wheel/upload/image", formData, {
      "Content-Type": "multipart/form-data",
    });
    if (response.status === 200) {
      Notify.setNotification({
        show: true,
        title: "アップロードが完了しました。",
        confirmLabel: "OK",
        actionsPosition: "center",
      });
    }
    return { data: response.data, status: response.status };
  } catch (error) {
    throw new Error(`Upload failed: ${error}`);
  }
};

/**
 * Deletes an image for a wheel.
 * @param {DeleteWheelImage} body - The request body containing the image details to delete.
 * @returns {Promise<{ data: any; status: number }>} A promise that resolves with the response data and status.
 */
export const deleteImageApi = async (
  body: DeleteWheelImage,
): Promise<{ data: any; status: number }> => {
  try {
    const response = await apiClient.post("/mst/item/detail/wheel/delete/image", body);
    if (response.status === 200) {
      Notify.setNotification({
        show: true,
        title: "削除が完了しました。",
        confirmLabel: "OK",
        actionsPosition: "center",
      });
    }
    return { data: response.data, status: response.status };
  } catch (error) {
    throw new Error(`Upload failed: ${error}`);
  }
};
